(function() {
	'use strict';

	angular
		.module('accesso-pay')
		.factory('AccessoPayPaymentService', AccessoPayPaymentService);

	/**
	 * Our AccessoPayPaymentService
	 * @param {Service} $document $document
	 * @param {Service} $http $http
	 * @param {Service} $injector $injector
	 * @param {Service} $q $q
	 * @param {Service} $rootScope $rootScope
	 * @param {Service} $location $location
	 * @param {Service} $state $state
	 * @param {Service} $timeout $timeout
	 * @param {Service} $window $window
	 * @param {Service} bugsnag Bugsnag
	 * @param {Service} AppData AppData
	 * @param {Service} AccessoPayUtils AccessoPayUtils
	 * @param {Service} AnalyticsDataLayerService Google Analytics Data Layer Service
	 * @param {Service} BillingRedirectService BillingRedirectService
	 * @param {Service} LegacyCart LegacyCart
	 * @param {Service} Enums Enums
	 * @param {Service} Language Language
	 * @param {Service} Modal Modal
	 * @param {Service} Purchase Purchase
	 * @param {Service} UtilsValidation UtilsValidation
	 * @param {Service} Utilities Utilities
	 * @param {Service} UserInfo UserInfo
	 * @returns {AccessoPayPaymentService} The instance
	 * @ngInject
	 */
	function AccessoPayPaymentService(
		$document,
		$http,
		$injector,
		$q,
		$rootScope,
		$location,
		$state,
		$timeout,
		$window,
		bugsnag,
		AppData,
		AccessoPayUtils,
		AnalyticsDataLayerService,
		BillingRedirectService,
		LegacyCart,
		Enums,
		Language,
		Modal,
		Purchase,
		UtilsValidation,
		Utilities,
		UserInfo
	) {
		var checkProp = Utilities.safelyCheckPropertyOnObject,
			isRealObject = UtilsValidation.isRealObject,
			returnOrDefault = UtilsValidation.returnOrDefault,
			validString = UtilsValidation.validString,
			validObject = UtilsValidation.validObject,
			getLocale = Language.getLocale,
			isFunction = angular.isFunction,
			savedPurchaseStatusEnums = Enums.savedPurchaseAuthStatuses;

		var service = {
			_destroyAccessoPayInstance: _destroyAccessoPayInstance,
			_revertAuthorizations: _revertAuthorizations,
			_routeToCart: _routeToCart,
			_showFailedPurchaseModal: _showFailedPurchaseModal,
			getDocument: getDocument,
			getTE2UserEmail: getTE2UserEmail,
			getWindow: getWindow,
			handlePendingPayments: handlePendingPayments,
			handleSavePurchaseError: handleSavePurchaseError,
			handleUpdateCartCheckoutInfoFailed: handleUpdateCartCheckoutInfoFailed,
			hasFailedSavedPayment: hasFailedSavedPayment,
			onPaymentComplete: onPaymentComplete,
			onPaymentFailedOrCancelled: onPaymentFailedOrCancelled,
			onPaymentMethodSelected: onPaymentMethodSelected,
			onPaymentRedirect: onPaymentRedirect,
			onPurchaseFailed: onPurchaseFailed,
			removeToolbarOverrideParam: removeToolbarOverrideParam,
		};

		return service;

		/**
		 * Leaves a bugsnag breadcrumb
		 * @param {String} msg The message
		 * @returns {undefined}
		 */
		function breadcrumb(msg) {
			if (checkProp('bugsnag.breadcrumb', bugsnag, angular.isFunction)) {
				bugsnag.breadcrumb('accesso Pay Payment Service: ' + msg);
			}

			$http.post(AppData.getApiUrl() + '/api/log', {
				m: msg,
				s: checkProp('AppData.cart.session_id', AppData, 'returnValue'),
				t: 'accesso-pay-payment-service-bread'
			});
		}
		/**
		 * Notify bugsnag
		 * @private
		 * @param {String} msg The message
		 * @returns {undefined}
		 */
		function notifyBugsnag(msg) {
			if (checkProp('bugsnag.notify', bugsnag, angular.isFunction)) {
				bugsnag.notify('accesso Pay Payment Service: ' + msg);
			}

			$http.post(AppData.getApiUrl() + '/api/log', {
				m: msg,
				s: checkProp('AppData.cart.session_id', AppData, 'returnValue'),
				t: 'accesso-pay-payment-service-notify'
			});
		}

		/**
		 * Executes on payment method selection event
		 * @public
		 * @memberof AccessoPayPaymentService
		 * @param {Event} event PaymentSelected event
		 * @see {@link https://pay.dev-na2.accessoticketing.com/docs/docs.html#data-models}
		 * @returns {undefined}
		 */
		function onPaymentMethodSelected(event) {
			breadcrumb('payment selected');
			AccessoPayUtils.broadcastAnalyticsEvent('triggerPaymentSelection', event.payload.type);
			AccessoPayUtils.broadcastAnalyticsEvent('triggerCheckoutPaymentInfo', event.payload.type);
		}

		/**
		 * Executes on payment failed/cancelled event
		 * @public
		 * @memberof AccessoPayPaymentService
		 * @see {@link https://pay.dev-na2.accessoticketing.com/docs/docs.html#data-models}
		 * @returns {undefined}
		 */
		function onPaymentFailedOrCancelled() {
			notifyBugsnag('failed or cancelled');
			$rootScope.showSpinner(getLocale('Purchase', 'savePurchaseAuthStatusLoadingMsg'));
			service.removeToolbarOverrideParam();

			Purchase
				.savePurchaseAuthStatus()
				.then(function handleAuthStatusResponse(response) {
					if (service.hasFailedSavedPayment(response)) {
						return Purchase.deleteSavedPurchase();
					}
				})
				.finally($rootScope.hideSpinner);
		}

		/**
		 * Checks if the saved payment is in failed status
		 * @public
		 * @param {Object} response savePurchaseAuthStatus response
		 * @returns {Boolean} true if payment in failed status
		 */
		function hasFailedSavedPayment(response) {
			if (!validObject(response) || response.status !== Enums.status.OK || !validString(response.saved_purchase_status)) {
				return false;
			}

			return [savedPurchaseStatusEnums.FAILED_STATUS, savedPurchaseStatusEnums.INCOMPLETE_STATUS].includes(response.saved_purchase_status);
		}

		/**
		 * Executes on payment redirect event
		 * @public
		 * @memberof AccessoPayPaymentService
		 * @param {Event} event PaymentRedirect event
		 * @see {@link https://pay.dev-na2.accessoticketing.com/docs/docs.html#data-models}
		 * @returns {undefined}
		 */
		function onPaymentRedirect(event) {
			var win = service.getWindow();
			var payload = event.payload,
				payment = UserInfo.get('payment'),
				shipping = UserInfo.get('shipping'),
				hasMultiPaymentInfo = checkProp('event.payload', event, 'validArray'),
				donations = UserInfo.get('donation'),
				insurance = UserInfo.get('insurance'),
				partialPayments = [],
				accessoPayPrimaryPayment;

			notifyBugsnag('payment redirect');

			$rootScope.showSpinner(getLocale('ApplicationLabels', 'updatingCartLoadMsg'));

			if (hasMultiPaymentInfo) {
				accessoPayPrimaryPayment = payload[0];
				if (payload[0].donation) {
					payment.donationAmount = parseFloat(payload[0].donation.donationAmount);
					payment.donationStatus = payload[0].donation.paymentStatus;
					payment.splitDonation = payload[0].donation.splitDonation;
				}
				if (payload[0].insurance && !payload[0].insurance.splitInsurance) {
					payment.premiumAmount = parseFloat(payload[0].insurance.premiumAmount);
					payment.insuranceStatus = payload[0].insurance.paymentStatus;
					payment.splitInsurance = payload[0].insurance.splitInsurance;
				}
				payload.forEach(function addPartialPayments(pp, index) {
					var partialPayment = {};
					if (index === 0) {
						return;
					}

					// eslint-disable-next-line camelcase
					partialPayment.auth_id = pp.authId;
					partialPayment.amount = pp.amount;
					partialPayment.paymentReference = pp.paymentReference;
					partialPayment.paymentMerchantId = pp.paymentMerchantId || '';
					partialPayment.paymentMethodCode = returnOrDefault(pp.paymentMethodCode, '', validString);
					partialPayment.paymentProviderName = returnOrDefault(pp.paymentProviderName, '', validString);
					partialPayment.rawCardBrand = returnOrDefault(pp.rawCardBrand, '', validString);
					partialPayment.isAccessoPayPayment = true;
					if (pp.donation) {
						partialPayment.donationAmount = parseFloat(pp.donation.donationAmount);
						partialPayment.donationStatus = pp.donation.paymentStatus;
						partialPayment.splitDonation = pp.donation.splitDonation;
					}
					if (pp.insurance) {
						partialPayment.premiumAmount = parseFloat(pp.insurance.premiumAmount);
						partialPayment.insuranceStatus = pp.insurance.paymentStatus;
						partialPayment.splitInsurance = pp.insurance.splitInsurance;
					}

					partialPayments.push(partialPayment);
				});

				UserInfo.set('partialPayments', partialPayments);
			} else {
				accessoPayPrimaryPayment = payload;
				if (payload.donation) {
					payment.donationAmount = parseFloat(donations.donationAmount);
					payment.donationStatus = donations.paymentStatus;
					payment.splitDonation = donations.splitDonation;
				}
				if (payload.insurance) {
					payment.premiumAmount = parseFloat(payload.insurance.premiumAmount);
					payment.insuranceStatus = payload.insurance.paymentStatus;
					payment.splitInsurance = payload.insurance.splitInsurance;
				}
			}

			// eslint-disable-next-line camelcase
			payment.auth_id = accessoPayPrimaryPayment.authId;
			payment.paymentReference = accessoPayPrimaryPayment.paymentReference;
			payment.paymentMerchantId = accessoPayPrimaryPayment.paymentMerchantId || '';
			payment.paymentMethodCode = returnOrDefault(accessoPayPrimaryPayment.paymentMethodCode, '', validString);
			payment.paymentProviderName = returnOrDefault(accessoPayPrimaryPayment.paymentProviderName, '', validString);
			payment.rawCardBrand = returnOrDefault(accessoPayPrimaryPayment.rawCardBrand, '', validString);
			payment.isAccessoPayPayment = true;
			if (angular.isNumber(accessoPayPrimaryPayment.amount)) {
				payment.paymentAmount = accessoPayPrimaryPayment.amount;
			}

			UserInfo.set('payment', payment);
			LegacyCart.callUpdateCartCheckoutInfo({
				// eslint-disable-next-line camelcase
				additional_attributes: [{
					name: 'accessoPay',
					value: 'true'
				}, {
					name: 'rawCardBrand',
					value: payment.rawCardBrand
				}],
				shippingAmount: shipping.deliveryAmount,
				shippingMethod: shipping.deliveryMethod
			}, function handleSavePurchase() {
				Purchase
					.deleteSavedPurchase()
					.finally(function doSavePurchase() {
						Purchase.savePurchase(
							function success(data) {
								var $doc = service.getDocument(),
									redirectFormId = 'acsp_rdfrm',
									redirectForm = $doc.getElementById(redirectFormId),
									// Query parameter returned by Adyen
									adyenRedirectHttpMethod = accessoPayPrimaryPayment.method,
									form,
									formField,
									key;

								if (checkProp('data.status', data, 'returnValue') === Enums.status.OK) {
									if (Utilities.isInIFrame()) {
										// Let accesso.js handle redirect
										win.parent.postMessage(JSON.stringify({
											messageType: Enums.messages.ACCESSO_PAY_REDIRECT,
											method: accessoPayPrimaryPayment.method,
											redirectParams: accessoPayPrimaryPayment.redirectParams,
											url: accessoPayPrimaryPayment.url
										}), '*');
									} else if (adyenRedirectHttpMethod === 'POST') {
										if (!redirectForm) {
											form = $doc.createElement('form');
											$doc.body.appendChild(form);
										}
										form.id = redirectFormId;
										form.name = redirectFormId;
										form.action = accessoPayPrimaryPayment.url;
										form.style.display = 'none';
										form.enctype = 'application/x-www-form-urlencoded';
										form.innerHTML = '';
										form.method = accessoPayPrimaryPayment.method;
										for (key in accessoPayPrimaryPayment.redirectParams) {
											formField = $doc.createElement('input');
											formField.type = 'hidden';
											formField.name = key;
											formField.value = accessoPayPrimaryPayment.redirectParams[key];

											form.appendChild(formField);
										}
										form.submit();
									} else {
										// Store is not in an embedded iframe and redirect
										// Assuming adyenRedirectHttpMethod = 'GET' or is undefined
										// Something to consider:  Error handling for undefined url; will break flow
										win.location.href = accessoPayPrimaryPayment.url;
									}
								} else {
									notifyBugsnag('savePurchase failed');
									service._revertAuthorizations();
								}
							},
							service.handleSavePurchaseError,
							true
						);
					})
			}, service.handleUpdateCartCheckoutInfoFailed.bind(service, 1));
		}

		/**
		 * Handle save purchase error
		 * @public
		 * @function
		 * @returns {undefined}
		 */
		function handleSavePurchaseError() {
			$rootScope.hideSpinner();
			breadcrumb('handle save purchase errored');

			Modal.displayModal({
				action: 'Save Purchase Error',
				buttonFlags: [Enums.buttonFlags.OK],
				callback: service._routeToCart,
				category: 'Save Purchase Error',
				description: getLocale('Alert', 'apersSavePurchaseErrorMessage'),
				label: getLocale('Alert', 'apersSavePurchaseErrorTitle'),
				showCloseButton: false,
				title: getLocale('Alert', 'apersSavePurchaseErrorTitle')
			});
		}

		/**
		 * Handle update cart checkout info failed
		 * @public
		 * @function
		 * @param {Number} errorLocation The error in which this happened
		 * @returns {undefined}
		 */
		function handleUpdateCartCheckoutInfoFailed(errorLocation) {
			var bugsnagMessage = 'accessoPay Payment Service: update cart checkout info failed';

			$rootScope.hideSpinner();
			Modal.displayModal({
				action: 'Error',
				buttonFlags: [Enums.buttonFlags.OK],
				callback: service._routeToCart,
				category: 'AccessoPay',
				description: getLocale('Alert', 'failedUpdateCheckoutInfoMsg'),
				title: getLocale('Alert', 'failedUpdateCheckoutInfoTitle')
			});
			service._revertAuthorizations();

			// eslint-disable-next-line default-case
			switch (errorLocation) {
			case 1:
				bugsnagMessage += ' - payment redirect';
				break;
			case 2:
				bugsnagMessage += ' - pending payment';
				break;
			}
			notifyBugsnag(bugsnagMessage);
		}

		/**
		 * Routes to the cart screen
		 * @private
		 * @returns {undefined}
		 */
		function _routeToCart() {
			Utilities.goTo(Enums.routes.CART_VIEW);
		}

		/**
		 * Destroy accesso Pay instance
		 * @private
		 * @memberof AccessoPayPaymentService
		 * @returns {undefined}
		 */
		function _destroyAccessoPayInstance() {
			var accessoPay = service.getWindow().accessoPay;

			if (isRealObject(accessoPay) && isFunction(accessoPay.destroy)) {
				accessoPay.destroy();
			}
		}

		/**
		 * Reverts authorizations
		 * @returns {undefined}
		 */
		function _revertAuthorizations() {
			var partialPayments = UserInfo.get('partialPayments');
			var payment = UserInfo.get('payment');
			var chain = $q.resolve();
			var service = Enums.services.CANCEL_PAYMENT;

			if (UtilsValidation.validArray(partialPayments)) {
				partialPayments.reduce(function revertParitalPayment(promChain, pmt) {
					if (checkProp('pmt.paymentReference', pmt, 'validString')) {
						return promChain.then(function() {
							return $q(function(resolve) {
								AppData.fetch(service, false, {
									amount: pmt.amount,
									// eslint-disable-next-line camelcase
									payment_reference: pmt.paymentReference,
									// eslint-disable-next-line camelcase
									payment_type: pmt.paymentMethodCode
								}).then(resolve).catch(resolve);
							});
						});
					}
					return promChain;
				}, chain);
			}

			if (checkProp('payment.paymentReference', payment, 'validString')) {
				chain.then(function revertMain() {
					// eslint-disable-next-line camelcase
					AppData.fetch(service, false, {
						amount: payment.paymentAmount || parseFloat(AccessoPayUtils.getAmountForPayment()),
						// eslint-disable-next-line camelcase
						payment_reference: payment.paymentReference,
						// eslint-disable-next-line camelcase
						payment_type: payment.paymentMethodCode
					});
				});
			}
		}

		/**
		 * Gets the window object
		 * @public
		 * @memberof AccessoPayPaymentService
		 * @returns {Object} The window object
		 */
		function getWindow() {
			return $window;
		}

		/**
		 * Returns document
		 * @public
		 * @memberof AccessoPayPaymentService
		 * @returns {Object} document
		 */
		function getDocument() {
			return $document[0];
		}

		/**
		 * Returns te2 user email
		 * @public
		 * @memberof AccessoPayPaymentService
		 * @returns {String} email address
		 */
		function getTE2UserEmail() {
			var TE2Service = $injector.has('TE2Service') ? $injector.get('TE2Service') : {},
				te2Settings = isFunction(TE2Service.getSettings) && TE2Service.getSettings();

			if (checkProp('te2Settings.TE2AccountInfo.emailAddress', te2Settings, validString)) {
				return te2Settings.TE2AccountInfo.emailAddress;
			}
			return '';
		}

		/**
		 * Removes url param to hide toolbar
		 * @public
		 * @memberof AccessoPayPaymentService
		 * @returns {undefined}
		 */
		function removeToolbarOverrideParam() {
			var win = service.getWindow(),
				paramsToClear = ['acsop_v5_pmt'],
				queryParams = $location.search();

			$rootScope.toolbarOverride = undefined;
			if (Utilities.isInIFrame()) {
				win.parent.postMessage(JSON.stringify({
					messageType: Enums.messages.ACCESSO_PAY_CLEAR_QUERY_PARAMS,
					queryParams: paramsToClear
				}), '*');
			}
			paramsToClear.forEach(function clearQueryParam(value) {
				delete queryParams[value];
			});

			$location.replace();
			$location.search(queryParams);
		}

		/**
		 * Executes on payment complete event
		 * @public
		 * @memberof AccessoPayPaymentService
		 * @param {Event} event PaymentComplete event
		 * @see {@link https://pay.dev-na2.accessoticketing.com/docs/docs.html#data-models}
		 * @returns {undefined}
		 */
		function onPaymentComplete(event) {
			var billing = UserInfo.get('billing'),
				payment = UserInfo.get('payment') || {},
				payload = event.payload,
				hasMultiPaymentInfo = checkProp('event.payload.paymentInfo', event, 'validArray'),
				accessoPayBilling,
				accessoPayPrimaryPayment,
				paymentReference,
				donations = UserInfo.get('donation'),
				insurance = UserInfo.get('insurance'),
				partialPayments = [];

			$rootScope.showSpinner(getLocale('ApplicationLabels', 'purchaseLoadMsg'));

			// If there are pending requests, wait for them to finish
			if ($rootScope.hasPendingRequest(Enums.services.UPDATE_CART_CHECKOUT_INFO)) {
				// Call this method again after 250ms
				$timeout(function onWait() {
					service.onPaymentComplete(event);
				}, 250);

				// Stop execution
				return;
			}

			notifyBugsnag('payment complete', {
				currency: AppData.currencyCode,
				id: payload.authId,
				total: parseFloat(AccessoPayUtils.getAmountForPayment())
			});
			if (event.payload.donation) {
				donations = event.payload.donation;
			}
			if (event.payload.insurance) {
				insurance = event.payload.insurance;
			}

			if (hasMultiPaymentInfo) {
				accessoPayPrimaryPayment = payload.paymentInfo[0].legacy;
				paymentReference = payload.paymentInfo[0].authId;
				accessoPayBilling = payload.paymentInfo[0].billing;
				if (angular.isNumber(payload.paymentInfo[0].amount)) {
					payment.paymentAmount = payload.paymentInfo[0].amount;
				}
				if (payload.paymentInfo[0].donation) {
					payment.donationAmount = parseFloat(payload.paymentInfo[0].donation.donationAmount);
					payment.donationStatus = payload.paymentInfo[0].donation.paymentStatus;
					payment.splitDonation = payload.paymentInfo[0].donation.splitDonation;
				}
				if (payload.paymentInfo[0].insurance) {
					payment.premiumAmount = parseFloat(payload.paymentInfo[0].insurance.premiumAmount);
					payment.insuranceStatus = payload.paymentInfo[0].insurance.paymentStatus;
					payment.splitInsurance = payload.paymentInfo[0].insurance.splitInsurance;
				}
				if (insurance && !insurance.premiumAmount) {
					insurance = { premiumAmount: insurance.quote, splitInsurance: insurance.splitInsurance };
				}
			} else {
				accessoPayBilling = payload.user.billing;
				accessoPayPrimaryPayment = payload.legacy;
				paymentReference = payload.authId;
				if (payload.donation) {
					payment.donationAmount = parseFloat(payload.donation.donationAmount);
					payment.donationStatus = payload.donation.paymentStatus;
					payment.splitDonation = payload.donation.splitDonation;
				} else {
					donations = payload.donation;
				}
				if (payload.insurance) {
					payment.premiumAmount = parseFloat(payload.insurance.premiumAmount);
					payment.insuranceStatus = payload.insurance.paymentStatus;
					payment.splitInsurance = payload.insurance.splitInsurance;
				} else {
					insurance = payload.insurance;
				}
			}

			billing.phone = accessoPayBilling.phone;
			billing.countryCode = accessoPayBilling.phoneCountryCode;
			billing.firstName = accessoPayBilling.firstName;
			billing.lastName = accessoPayBilling.lastName;
			billing.address = accessoPayBilling.address1;
			billing.address2 = accessoPayBilling.address2;
			billing.city = accessoPayBilling.city;
			billing.state = accessoPayBilling.stateProvince;
			billing.zip = accessoPayBilling.zipPostal;
			billing.email = returnOrDefault(accessoPayBilling.email, service.getTE2UserEmail(), validString);
			billing.country = accessoPayBilling.country;

			// eslint-disable-next-line camelcase
			payment.auth_id = accessoPayPrimaryPayment.authId;
			payment.paymentReference = paymentReference;
			payment.paymentMerchantId = accessoPayPrimaryPayment.paymentMerchantId || '';
			payment.paymentMethodCode = returnOrDefault(accessoPayPrimaryPayment.paymentMethodCode, '', validString);
			payment.paymentProviderName = returnOrDefault(accessoPayPrimaryPayment.paymentProviderName, '', validString);
			payment.rawCardBrand = returnOrDefault(accessoPayPrimaryPayment.rawCardBrand, '', validString);
			payment.isAccessoPayPayment = true;

			if (hasMultiPaymentInfo) {
				payload.paymentInfo.forEach(function addPartialPayments(pp, index) {
					var ppLegacy = pp.legacy,
						partialPayment = {};
					if (index === 0) {
						return;
					}

					// eslint-disable-next-line camelcase
					partialPayment.auth_id = ppLegacy.authId;
					partialPayment.amount = pp.amount;
					partialPayment.paymentReference = pp.authId;
					partialPayment.paymentMerchantId = ppLegacy.paymentMerchantId || '';
					partialPayment.paymentMethodCode = returnOrDefault(ppLegacy.paymentMethodCode, '', validString);
					partialPayment.paymentProviderName = returnOrDefault(ppLegacy.paymentProviderName, '', validString);
					partialPayment.rawCardBrand = returnOrDefault(ppLegacy.rawCardBrand, '', validString);
					partialPayment.isAccessoPayPayment = true;
					if (pp.donation) {
						partialPayment.donationAmount = parseFloat(pp.donation.donationAmount);
						partialPayment.donationStatus = pp.donation.paymentStatus;
						partialPayment.splitDonation = pp.donation.splitDonation;
					}
					if (pp.insurance) {
						partialPayment.premiumAmount = parseFloat(pp.insurance.premiumAmount);
						partialPayment.insuranceStatus = pp.insurance.paymentStatus;
						partialPayment.splitInsurance = pp.insurance.splitInsurance;
					}

					partialPayments.push(partialPayment);
				});

				UserInfo.set('partialPayments', partialPayments);
			}

			UserInfo.set('donation', donations);
			UserInfo.set('insurance', insurance);
			UserInfo.set('payment', payment);
			UserInfo.set('billing', billing);

			service._destroyAccessoPayInstance();
			service.removeToolbarOverrideParam();

			if (hasMultiPaymentInfo) {
				if (payload.paymentInfo[0].awaitingAuthorization) {
					return service.handlePendingPayments();
				}
			} else if (payload.awaitingAuthorization) {
				return service.handlePendingPayments();
			}

			// Sends Email to GTM Data Layer
			AnalyticsDataLayerService.triggerAccessoPayBillingInformation(billing);

			Purchase.executePurchase(Purchase.defaultPostPurchaseSuccess, undefined, undefined, service.onPurchaseFailed).catch(service.onPurchaseFailed);
		}

		/**
		 * On purchase fail callback
		 * @param {String} errorCode The error code
		 * @returns {undefined}
		 */
		function onPurchaseFailed(errorCode) {
			var code = Enums.purchaseError;

			notifyBugsnag('purchase failed (' + errorCode + ')')

			switch (errorCode) {
			case code.PENDING:
			case code.ERROR_116:
			case code.ERROR_161:
			case code.ERROR_700:
			case code.MODAL:
				// These codes already display a message when its thrown
				service._revertAuthorizations();
				return;
			case code.CART:
				service._routeToCart();
				break;
			case code.GATHER_PURCHASE_DETAILS:
			case code.DISCONNECT:
			case code.CATCH:
			default:
				service._revertAuthorizations();
				service._showFailedPurchaseModal(errorCode);
				return;
			}
		}

		/**
		 * Shows the failed purchase modal. on click it routes them to the cart
		 * @param {Number|String} errorCode The error code
		 * @returns {undefined}
		 */
		function _showFailedPurchaseModal(errorCode) {
			Modal.displayModal({
				action: 'Error',
				buttonFlags: [Enums.buttonFlags.OK],
				callback: service._routeToCart,
				category: 'accessoPay',
				description: getLocale('Alert', 'failedPurchaseMsg'),
				icon: 'icon-info red',
				label: 'error: ' + errorCode,
				title: getLocale('Alert', 'failedPurchaseTitle')
			});
		}

		/**
		 * Handles payments with pending authroization
		 * @public
		 * @memberof AccessoPayPaymentService
		 * @returns {undefined}
		 */
		function handlePendingPayments() {
			var payment = UserInfo.get('payment') || {},
				shipping = UserInfo.get('shipping') || {};

			notifyBugsnag('pending payments');

			$rootScope.showSpinner(getLocale('Purchase', 'savePurchaseAuthStatusLoadingMsg'));

			Purchase
				.savePurchaseAuthStatus()
				.then(function handleAuthStatusResponse(response) {
					if (response.status === Enums.status.OK && validString(response.saved_purchase_status)) {
						if (response.saved_purchase_status === savedPurchaseStatusEnums.NO_SAVED_PURCHASE_STATUS) {
							LegacyCart.callUpdateCartCheckoutInfo({
								// eslint-disable-next-line camelcase
								additional_attributes: [{
									name: 'accessoPay',
									value: 'true'
								}, {
									name: 'rawCardBrand',
									value: payment.rawCardBrand
								}],
								shippingAmount: shipping.deliveryAmount,
								shippingMethod: shipping.deliveryMethod
							}, function handleSavePurchase() {
								Purchase.savePurchase(
									function success() {
										BillingRedirectService.finalizeRedirectTransaction(true, 'pending-order');
									},
									service.handleSavePurchaseError,
									true
								)
							}, service.handleUpdateCartCheckoutInfoFailed.bind(service, 2));
						} else {
							BillingRedirectService.finalizeRedirectTransaction(true, 'pending-order');
						}
					} else {
						$rootScope.routeTo($state.href('pending-order'));
					}
				});
		}
	}
}());